import { useCallback, useContext, useEffect, useState } from "react";
import { SocketContext } from "@contexts/SocketContext";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import EndpointsEnum from "@models/EndpointsEnum";
import ISerial from "@models/ISerial";
import IProcessPOShipmentItemSerialsDTO from "@models/DTO/IProcessPOShipmentItemSerialsDTO";
import IOnProcessPOShipmentItemSerialsDTO from "@models/DTO/IOnProcessPOShipmentItemSerialsDTO";
import IValidateSerialsDTO from "@models/DTO/IValidateSerialsDTO";
import IOnValidateSerialsDTO from "@models/DTO/IOnValidateSerialsDTO";

export interface IPendingSerial {
    value: string;
    isValid: boolean | null; // null = validating, true = valid, false = invalid
    error?: string;
}

export interface ISerialEntryItemState {
    serials: ISerial[];
    pendingSerials: IPendingSerial[];
    errors: string[];
    isSubmitting: boolean;
}

export interface ISerialEntryState {
    [catalogItemId: number]: ISerialEntryItemState;
}

const defaultItemState: ISerialEntryItemState = {
    serials: [],
    pendingSerials: [],
    errors: [],
    isSubmitting: false,
};

interface UsePurchaseOrderShipmentSerialsProps {
    poShipmentGuid: string;
}

export const usePurchaseOrderShipmentSerials = ({ poShipmentGuid }: UsePurchaseOrderShipmentSerialsProps) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;

    const [serialState, setSerialState] = useState<ISerialEntryState>({});

    const getItemState = useCallback(
        (catalogItemId: number): ISerialEntryItemState => {
            return serialState[catalogItemId] || defaultItemState;
        },
        [serialState]
    );

    const addPendingSerial = useCallback(
        (catalogItemId: number, value: string) => {
            const trimmed = value.trim();
            if (!trimmed) return;

            setSerialState((prev) => {
                const itemState = prev[catalogItemId] || defaultItemState;

                // Check for local duplicates against pending and created serials
                const existingPending = itemState.pendingSerials.some((p) => p.value === trimmed);
                const existingCreated = itemState.serials.some((s) => s.value === trimmed);
                if (existingPending || existingCreated) return prev;

                return {
                    ...prev,
                    [catalogItemId]: {
                        ...itemState,
                        pendingSerials: [...itemState.pendingSerials, { value: trimmed, isValid: null }],
                    },
                };
            });

            // Emit validation request to server
            const payload: IValidateSerialsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                serials: [trimmed],
            };
            socket.emit(EndpointsEnum.SerialValidateSerials, payload);
        },
        [userModel.guid, userModel.token, supplierModel.guid, socket]
    );

    const addBulkPendingSerials = useCallback((catalogItemId: number, values: string[]) => {
        setSerialState((prev) => {
            const itemState = prev[catalogItemId] || defaultItemState;
            const existingValues = new Set([...itemState.pendingSerials.map((p) => p.value), ...itemState.serials.map((s) => s.value)]);

            const newPending: IPendingSerial[] = values.filter((v) => !existingValues.has(v)).map((value) => ({ value, isValid: true }));

            if (newPending.length === 0) return prev;

            return {
                ...prev,
                [catalogItemId]: {
                    ...itemState,
                    pendingSerials: [...itemState.pendingSerials, ...newPending],
                },
            };
        });
    }, []);

    const removePendingSerial = useCallback((catalogItemId: number, index: number) => {
        setSerialState((prev) => {
            const itemState = prev[catalogItemId] || defaultItemState;
            return {
                ...prev,
                [catalogItemId]: {
                    ...itemState,
                    pendingSerials: itemState.pendingSerials.filter((_, i) => i !== index),
                },
            };
        });
    }, []);

    const submitSerials = useCallback(
        (catalogItemId: number) => {
            const currentState = serialState[catalogItemId] || defaultItemState;
            const validSerials = currentState.pendingSerials.filter((p) => p.isValid === true);
            if (validSerials.length === 0 || currentState.isSubmitting) return;

            const payload: IProcessPOShipmentItemSerialsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                poShipmentGuid,
                catalogItemId,
                serials: validSerials.map((p) => p.value),
            };

            setSerialState((prev) => ({
                ...prev,
                [catalogItemId]: {
                    ...(prev[catalogItemId] || defaultItemState),
                    isSubmitting: true,
                    errors: [],
                },
            }));

            socket.emit(EndpointsEnum.PurchaseOrderProcessPOShipmentItemSerials, payload);
        },
        [serialState, userModel.guid, userModel.token, supplierModel.guid, poShipmentGuid, socket]
    );

    const clearErrors = useCallback((catalogItemId: number) => {
        setSerialState((prev) => {
            const itemState = prev[catalogItemId];
            if (!itemState) return prev;
            return {
                ...prev,
                [catalogItemId]: {
                    ...itemState,
                    errors: [],
                },
            };
        });
    }, []);

    useEffect(() => {
        const onProcessPOShipmentItemSerials = (data: IOnProcessPOShipmentItemSerialsDTO) => {
            const catalogItemId = data.catalogItemId;

            setSerialState((prev) => {
                const itemState = prev[catalogItemId] || defaultItemState;

                const createdValues = new Set(data.serials.map((s) => s.value));
                const remainingPending = itemState.pendingSerials.filter((p) => !createdValues.has(p.value));

                return {
                    ...prev,
                    [catalogItemId]: {
                        serials: [...itemState.serials, ...data.serials],
                        pendingSerials: remainingPending,
                        errors: data.errors || [],
                        isSubmitting: false,
                    },
                };
            });
        };

        const onValidateSerials = (data: IOnValidateSerialsDTO) => {
            setSerialState((prev) => {
                const next = { ...prev };
                let changed = false;

                for (const result of data.results) {
                    // Find which catalog item has this pending serial
                    for (const catalogItemId of Object.keys(next)) {
                        const id = Number(catalogItemId);
                        const itemState = next[id];
                        if (!itemState) continue;

                        const pendingIndex = itemState.pendingSerials.findIndex((p) => p.value === result.serial && p.isValid === null);

                        if (pendingIndex !== -1) {
                            const updatedPending = [...itemState.pendingSerials];
                            updatedPending[pendingIndex] = {
                                ...updatedPending[pendingIndex],
                                isValid: result.isValid,
                                error: result.error,
                            };
                            next[id] = {
                                ...itemState,
                                pendingSerials: updatedPending,
                            };
                            changed = true;
                            break;
                        }
                    }
                }

                return changed ? next : prev;
            });
        };

        socket.on(EndpointsEnum.PurchaseOrderOnProcessPOShipmentItemSerials, onProcessPOShipmentItemSerials);
        socket.on(EndpointsEnum.SerialOnValidateSerials, onValidateSerials);

        return () => {
            socket.off(EndpointsEnum.PurchaseOrderOnProcessPOShipmentItemSerials, onProcessPOShipmentItemSerials);
            socket.off(EndpointsEnum.SerialOnValidateSerials, onValidateSerials);
        };
    }, [socket]);

    return {
        serialState,
        getItemState,
        addPendingSerial,
        addBulkPendingSerials,
        removePendingSerial,
        submitSerials,
        clearErrors,
    };
};
